import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Activity, Users, TrendingUp, AlertTriangle, Shield, FileDown, GitCompare, Loader2, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/Toast';
import AIActionPlanner from '../components/AIActionPlanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const RISK_COLORS = { low: '#10B981', medium: '#F59E0B', high: '#EF4444' };
const PIE_COLORS = ['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#CCFBF1'];

export default function AnalyticsPage() {
  const { show, node } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/analytics/overview`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePdf = async () => {
    try {
      const resp = await axios.get(`${API}/analytics/report/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Udvega_Executive_Summary.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      show('Executive summary downloaded');
    } catch {
      show('Failed to generate PDF', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!data || !data.has_data) {
    return (
      <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="analytics-empty">
        <h1 className="font-heading text-3xl font-semibold text-slate-800 mb-6">Team Analytics</h1>
        <div className="glass-card rounded-2xl p-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-2">No survey data yet.</p>
          <p className="text-sm text-slate-400">Analytics populate as employees complete their reviews.</p>
        </div>
        {node}
      </div>
    );
  }

  const visibleDepts = (data.departments || []).filter((d) => !d.privacy_locked);
  const pieData = visibleDepts.map((d) => ({ name: d.name, value: d.respondents }));
  const metrics = [
    { label: 'Workforce Wellness', value: `${Math.round(data.overall_wellness)}%`, icon: Activity },
    { label: 'Total Responses', value: data.total_results, icon: Users },
    { label: 'High Risk', value: data.total_high_risk, icon: AlertTriangle },
    { label: 'Prediction Accuracy', value: `${data.prediction_accuracy}%+`, icon: TrendingUp },
  ];

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-800">Team Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Aggregated, anonymized workforce insights</p>
        </div>
        <div className="flex gap-3">
          <Link to="/compare">
            <Button variant="outline" className="border-slate-300">
              <GitCompare className="w-4 h-4 mr-2" /> Compare Reviews
            </Button>
          </Link>
          <Button onClick={handlePdf} className="bg-teal-600 hover:bg-teal-700 text-white">
            <FileDown className="w-4 h-4 mr-2" /> Executive Summary
          </Button>
        </div>
      </div>

      <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((m) => (
          <motion.div key={m.label} variants={fadeUp} className="glass-card rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <m.icon className="w-5 h-5 text-teal-600" />
              </div>
              <span className="text-sm text-slate-500">{m.label}</span>
            </div>
            <p className="font-heading text-3xl font-semibold text-slate-800">{m.value}</p>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Burnout drivers */}
        <div className="glass-card rounded-3xl p-6" data-testid="burnout-drivers-chart">
          <h3 className="font-heading text-lg font-medium text-slate-800 mb-6">Top Burnout Drivers</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.burnout_drivers} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#94A3B8' }} />
              <YAxis type="category" dataKey="factor" tick={{ fontSize: 11, fill: '#64748B' }} width={140} />
              <Tooltip contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }} labelStyle={{ color: '#94A3B8' }} />
              <Bar dataKey="impact" fill="#0D9488" radius={[0, 8, 8, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Distribution */}
        <div className="glass-card rounded-3xl p-6" data-testid="team-distribution">
          <h3 className="font-heading text-lg font-medium text-slate-800 mb-4">Response Distribution</h3>
          {pieData.length === 0 ? (
            <p className="text-sm text-slate-400 py-16 text-center">
              Departments appear here once they pass the privacy threshold (2+ responses).
            </p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" stroke="none">
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1E293B', border: 'none', borderRadius: '12px', color: '#fff', fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {pieData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-xs text-slate-500">{d.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Department heatmap */}
      <div className="glass-card rounded-3xl p-6" data-testid="department-heatmap">
        <div className="flex items-center gap-2 mb-5">
          <Shield className="w-5 h-5 text-slate-600" />
          <h3 className="font-heading text-lg font-medium text-slate-800">Department Health Overview</h3>
        </div>
        {visibleDepts.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">
            No department has reached the privacy threshold (2+ responses) yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {visibleDepts.map((dept) => (
              <div
                key={dept.name}
                className="p-4 rounded-2xl border transition-all hover:shadow-md"
                style={{
                  borderColor: RISK_COLORS[dept.risk_level] + '40',
                  background: RISK_COLORS[dept.risk_level] + '08',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{dept.name}</span>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: RISK_COLORS[dept.risk_level] }} />
                </div>
                <p className="font-heading text-2xl font-semibold text-slate-800">{Math.round(dept.wellness_score)}%</p>
                <p className="text-xs text-slate-400">
                  {dept.respondents} respondents · {dept.high_risk_pct}% high risk
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <AIActionPlanner analytics={data} enabled />
      </div>

      <div className="mt-6 p-4 rounded-2xl bg-teal-50 border border-teal-100 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
        <p className="text-sm text-teal-800">
          <strong>Privacy Notice:</strong> All data is aggregated at the team level. Individual responses are anonymized and never visible.
        </p>
      </div>
      {node}
    </div>
  );
}
